<script setup lang="ts">
import checkoutContent from "~/content/checkout.json";
import type { Cart, CartItem } from "~/stores/orders";
import { USER_ORDER_PAYMENT_STATUS } from "~/utils/premium/constants";
import { isActiveCostCentre } from "~/utils/isActiveCostCentre";
import { rescaleModifiersToLine } from "~/utils/lineModifierQuantities";
import { isSameStoredModifierSet } from "~/utils/prepareItemsForCart";
import { clampSplitQuantityModifiers } from "~/utils/clampSplitQuantityModifiers";
import type { AppliedModifier } from "~/types";

const ordersStore = useOrdersStore();
const makersStore = useMakersStore();
const deliveryStore = useDeliveryStore();
const discountStore = useDiscountStore();
const authStore = useAuthStore();
const premiumStore = usePremiumStore();
const route = useRoute();

interface Props {
  disableInputs?: boolean;
  // Group-order menu customisation: true while a cart line is no longer allowed by the
  // frozen menu. Disables the checkout button + shows an inline prompt to remove it.
  //
  // Prefer `orderabilityBlockedEffective` below over reading this directly — the prop is
  // absent on the layout-rendered mobile overlay, and a half-applied block (greyed lines,
  // no banner, live checkout button) is worse than none.
  orderabilityBlocked?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  disableInputs: false,
  orderabilityBlocked: false,
});

// Effective lock: explicit prop (desktop sidebar wiring) OR store flag
// (set by /checkout when step >= 1, also obeyed by the layout-rendered
// mobile cart overlay which receives no prop).
const cartIsLocked = computed(() => props.disableInputs || ordersStore.cartLocked);

function backToMaker() {
  const c = ordersStore.cart as Record<string, unknown> | null;
  const maker = c?.maker as Record<string, unknown> | undefined;
  const slug = maker?.slug as string | undefined;
  if (!slug) return;
  // Drop the lock + close the mobile overlay before navigating so the maker
  // page mounts with editable cart inputs again. /checkout's onBeforeUnmount
  // also clears the lock as a safety net.
  ordersStore.setCartLocked(false);
  ordersStore.setShowCart(false);
  const isPremiumTarget = ordersStore.cartType === "premium";
  const target = isPremiumTarget ? `/premium/store/${slug}` : `/store/${slug}`;
  // /premium/store/[slug] is a forward-only route — never let the browser
  // back button rewind into it after the customer leaves.
  navigateTo(target, isPremiumTarget ? { replace: true } : undefined);
}

const emit = defineEmits<{
  "checkout:start": [];
}>();

const shareLink = ref(false);
const activeAccordionSection = ref<string | null>(null);
const itemsFlash = ref(false);
const showDeliveryTooltip = ref(false);
const showFullPremBreakdownTooltip = ref(false);
const isMobile = ref(false);
let tooltipHideTimeout: ReturnType<typeof setTimeout> | null = null;
let deliveryTooltipHideTimeout: ReturnType<typeof setTimeout> | null = null;

const cart = computed(() => ordersStore.cart);
const items = computed(() => cart.value?.items ?? []);
const isPremium = computed(() => ordersStore.cartType === "premium");
const isEditMode = computed(() => isPremium.value || ordersStore.cartMode === "edit");

// Only GROUP SELECT orders have a frozen menu to drift from, so only they can be flagged.
// Everything in the block below scopes on this rather than `isPremium`: a Set order shares
// the premium cart type, and `orderabilityFailures` cannot say which order it described, so
// a group order's leftover verdict would grey out a Set order's lines and block its
// checkout with no control anywhere to clear it. Same scope as the store page's
// `orderabilityBlocked` and the checkout guard in `ShoppingCart/index.vue`.
const isGroupOrderCart = computed(
  () => isPremium.value && !!premiumStore.selectedPremiumOrderConfig?.isGroupOrder,
);

// Cart lines the frozen menu no longer allows (menu customisation). Read straight from the
// store rather than a prop, so the mobile cart overlay — rendered by the layout, which
// passes no props — flags the same lines as the desktop sidebar.
// Ids are normalised through `Number()` on BOTH sides of every comparison in this block.
// The backend sends `product_id` as a JSON number and the cart line's `product.id` is
// usually one too, but "usually" was doing real work here: `isLineUnavailable` used to
// require `typeof productId === "number"`, so a string id made it silently return false —
// failing OPEN, quietly re-enabling the increment on a flagged line — while the name lookup
// below used `===` and the store page cast with `as number`. Three treatments of one value.
// All three now go through `Number()`: the store page's notification lookup was the last
// holdout and was fixed 2026-09-16. Keep it that way — this value crosses two payloads.
//
// A failure is NOT always a verdict on the whole product. `modifier_violation` names the
// selected OPTIONS the frozen menu rejects (`modifier_ids`), and a cart can hold two lines
// of one dish with different option sets — `addCartItem` only merges lines whose modifiers
// match, so "Pizza + olives" and "Pizza + cheese" are separate lines. Flattening every
// failure to a product id disabled the plus control on the line that was perfectly fine.
// Every OTHER reason (not_approved, inactive, uncategorized, rule_violation, maker_mismatch,
// not_found, modifier_unsatisfiable) really does condemn the product, so those stay
// product-wide. Split into two lookups rather than one, because they answer different
// questions.
const PRODUCT_WIDE_FAILURE = (failure: { reason: string; modifier_ids?: number[] }) =>
  failure.reason !== "modifier_violation" || !failure.modifier_ids?.length;

const unavailableProductIds = computed(
  () =>
    new Set(
      premiumStore.orderabilityFailures
        .filter(PRODUCT_WIDE_FAILURE)
        .map((failure) => Number(failure.product_id)),
    ),
);

// product id → the option ids that condemn a line carrying any of them. An option is
// offending because the option itself is inactive or breaks an allergen rule — a property
// of the option, not of the line — so "this line holds one of them" is the whole test.
// There is no line id to match on: `checkOrderability` takes ad-hoc `{product_id,
// appliedModifiers}` items and the response carries no line identity, so the modifier ids
// are the only thing tying a failure back to the line that caused it.
const unavailableModifierIdsByProduct = computed(() => {
  const byProduct = new Map<number, Set<number>>();
  for (const failure of premiumStore.orderabilityFailures) {
    if (PRODUCT_WIDE_FAILURE(failure)) continue;
    const productId = Number(failure.product_id);
    const offending = byProduct.get(productId) ?? new Set<number>();
    for (const modifierId of failure.modifier_ids ?? []) offending.add(Number(modifierId));
    byProduct.set(productId, offending);
  }
  return byProduct;
});

// The block, from either source. The lines already grey themselves out from the store (the
// overlay gets no props), but the banner and the checkout button read the PROP — so a
// propless mount greyed the lines, showed no banner and left checkout clickable. Nothing
// reaches that path today because `pageOwnsMobileCart` covers `premium-store`, but the two
// halves of one block should not be able to disagree depending on where the cart is
// mounted. Same shape as the bug #178 fixed.
const orderabilityBlockedEffective = computed(
  () => props.orderabilityBlocked || (isGroupOrderCart.value && premiumStore.orderabilityFailures.length > 0),
);

// The affected dish names, so the banner can say WHICH dish to remove rather than "some
// items". Falls back to the product id if a failing line somehow isn't in the cart — a
// vague banner beats none.
//
// Deduped by product id, not mapped per failure: two lines of one dish with different
// option sets produce two failures, and "Pizza, Pizza — remove them" reads like a bug. The
// singular/plural in the banner counts THIS list for the same reason.
const unavailableProductNameList = computed(() => {
  if (!isGroupOrderCart.value || !premiumStore.orderabilityFailures.length) return [] as string[];
  const seen = new Set<number>();
  const names: string[] = [];
  for (const failure of premiumStore.orderabilityFailures) {
    const productId = Number(failure.product_id);
    if (seen.has(productId)) continue;
    seen.add(productId);
    const cartLine = items.value.find(
      (cartItem: CartItem) => Number(cartItem.product?.id) === productId,
    );
    names.push(cartLine?.product?.name || `#${failure.product_id}`);
  }
  return names;
});

const unavailableProductNames = computed(() => unavailableProductNameList.value.join(", "));

// An unavailable line keeps remove and decrease so the customer can resolve it themselves;
// only increase is blocked — ordering MORE of a dish the backend will reject at checkout
// helps nobody. Non-premium carts are never flagged.
function isLineUnavailable(item: Record<string, unknown>) {
  if (!isGroupOrderCart.value) return false;
  const productId = (item.product as Record<string, unknown> | undefined)?.id;
  if (productId === null || productId === undefined) return false;
  const id = Number(productId);
  if (unavailableProductIds.value.has(id)) return true;
  const offendingModifierIds = unavailableModifierIdsByProduct.value.get(id);
  if (!offendingModifierIds?.size) return false;
  const applied = (item.appliedModifiers as Array<{ modifier_id: number }> | undefined) ?? [];
  return applied.some((modifier) => offendingModifierIds.has(Number(modifier.modifier_id)));
}

const hasCartContext = computed(() => Boolean((cart.value as Record<string, unknown> | undefined)?.maker && ((cart.value as Record<string, unknown>).maker as Record<string, unknown>)?.slug));
const isEditModeHydrating = computed(() => ordersStore.cartMode === "edit" && !hasCartContext.value);
const isCheckout = computed(() => route.name === "checkout" && !ordersStore.showCart);
const isStorePage = computed(() => String(route.name).startsWith("store"));
const hasCostCentres = computed(
  () => Array.isArray(authStore.customerCostCentres) && authStore.customerCostCentres.some(isActiveCostCentre),
);

// Ensure delivery windows are generated if a date is set but windows are missing
// (handles carts loaded from localStorage before the setDeliveryWindows feature)
onMounted(() => {
  isMobile.value = window.innerWidth <= 768;
  window.addEventListener('resize', () => {
    isMobile.value = window.innerWidth <= 768;
  });

  const c = cart.value;
  if (c && c.date && typeof c.date === 'object') {
    const dateObj = c.date as Record<string, unknown>;
    const windows = dateObj.deliveryWindows as unknown[] | undefined;
    if (!windows || windows.length === 0) {
      ordersStore.setDeliveryWindows(dateObj);
    }
  }
});

const showCartItems = computed(() => {
  if (isEditMode.value) return isEditModeHydrating.value || hasCartContext.value;
  return items.value.length > 0;
});

const chosenMaker = computed(() => makersStore.chosenMaker || ({} as Record<string, unknown>));

const makerDeliveryDays = computed(() => {
  return makersStore.makerDeliveryDays || false;
});

const dateTimeSummary = computed(() => {
  const date = cart.value?.date as Record<string, unknown> | undefined;
  if (!date) return "Select date & time";
  const datePart = date.string || date.full || "";
  const window = date.selectedWindow as string || "";
  const timeStr = window ? `${window}` : "No time selected";
  return `${datePart}, ${timeStr}`;
});

const itemsSummary = computed(() => {
  const count = items.value.length;
  if (count === 0) return "No items";
  return `${count} item${count > 1 ? "s" : ""}`;
});

const isOrderCreated = computed(() => Boolean(ordersStore.order && (ordersStore.order as Record<string, unknown>).id));

const hideTotalUntilOrderCreated = computed(() => {
  return authStore.isLoggedIn && hasCostCentres.value && !isOrderCreated.value;
});

const showFullBreakdown = computed(() => {
  if (isPremium.value) return true;
  return Boolean(cart.value && Number((cart.value as Record<string, unknown>).total || 0) > 0);
});

const subtotal = computed(() => {
  const title = !isPremium.value ? "Subtotal (Exc VAT):" : "Subtotal:";
  const net = Number(cart.value?.items_net || 0);
  const value = `£${(net / 100).toFixed(2)}`;
  return { title, value };
});

const deliveryFee = computed(() => {
  if (isPremium.value) {
    if (isGroupOrder.value) return '';
    const chosenOrd = ordersStore.chosenOrder as Record<string, unknown> | false;
    if (chosenOrd && typeof chosenOrd === 'object') {
      const gross = Number((chosenOrd as Record<string, unknown>).delivery_gross || 0);
      if (gross > 0) return `£${(gross / 100).toFixed(2)}`;
    }
    return false;
  }
  if (!isPremium.value) {
    const orderObj = ordersStore.order as Record<string, unknown> | false;
    if (orderObj && typeof orderObj === 'object' && orderObj.delivery_net !== undefined) {
      return `£${(Number(orderObj.delivery_net || 0) / 100).toFixed(2)}`;
    }
    const net = Number(cart.value?.delivery_net || 0);
    return `£${(net / 100).toFixed(2)}`;
  }
  return false;
});

const serviceFeeRate = computed(() => {
  const orderObj = ordersStore.order as Record<string, unknown> | false;
  const rate = (orderObj && typeof orderObj === 'object' && orderObj.service_fee_rate)
    || (cart.value as Record<string, unknown>)?.service_fee_rate;
  return rate ? `${rate}%` : null;
});

const serviceFee = computed(() => {
  const orderObj = ordersStore.order as Record<string, unknown> | false;
  const feeNet = (orderObj && typeof orderObj === 'object' && orderObj.service_fee_net)
    || (cart.value as Record<string, unknown>)?.service_fee_net;
  if (!feeNet) return null;
  return `£${(Number(feeNet) / 100).toFixed(2)}`;
});

const displayVat = computed(() => {
  // Always the order's FULL VAT (items + delivery + service + fees). The
  // breakdown lists net line items against a discount-aware gross Total,
  // so anything less than total_vat won't reconcile — this was previously
  // special-cased to items_vat for cost-centre customers, which showed
  // £0.00 for zero-rated food and understated VAT elsewhere. Matches the
  // success/payment/modal breakdowns. Prefer the costed order; the cart's
  // running totals aren't costed by the backend so total_vat stays 0 there.
  const orderObj = ordersStore.order as Record<string, unknown> | false;
  const source =
    orderObj && typeof orderObj === "object"
      ? orderObj
      : (cart.value as Record<string, unknown>);
  return (Number(source?.total_vat || 0) / 100).toFixed(2);
});

const discountAmount = computed(() => {
  // Discount lives on the persisted order, not the cart. Surface a line
  // when present so the customer sees the deduction they just applied.
  const orderObj = ordersStore.order as Record<string, unknown> | false;
  if (!orderObj || typeof orderObj !== "object") return null;
  const gross = Number(orderObj.discount_gross || 0);
  if (gross <= 0) return null;
  return `-£${(gross / 100).toFixed(2)}`;
});

const cartTotal = computed(() => {
  if (!isPremium.value) {
    // Prefer the persisted order's `total` once an order exists — that's
    // the discount-aware figure. Falling back to the cart's running total
    // matches the pre-checkout (no order yet) view.
    const orderObj = ordersStore.order as Record<string, unknown> | false;
    const orderTotal = orderObj && typeof orderObj === "object" ? Number(orderObj.total || 0) : 0;
    if (orderTotal > 0) return `£${(orderTotal / 100).toFixed(2)}`;
    const total = Number(cart.value?.total || cart.value?.total_gross || 0);
    return `£${(total / 100).toFixed(2)}`;
  }
  if (isGroupOrder.value) {
    const empGross = Number(premiumOrderCredit.value?.employee_gross || 0);
    return empGross === 0 || isUserOwner.value
      ? '£0.00'
      : `£${(Math.abs(empGross) / 100).toFixed(2)}`;
  }
  // Premium solo: items_net + delivery_gross
  const itemsNet = Number(cart.value?.items_net || 0);
  const chosenOrd = ordersStore.chosenOrder as Record<string, unknown> | false;
  const delGross = chosenOrd && typeof chosenOrd === 'object'
    ? Number((chosenOrd as Record<string, unknown>).delivery_gross || 0)
    : 0;
  return `£${((itemsNet + delGross) / 100).toFixed(2)}`;
});

const minOrderVal = computed(() => {
  const maker = (cart.value as Record<string, unknown> | undefined)?.maker as Record<string, unknown> | undefined;
  return Number(maker?.min_order_amount || 0) / 100 || 15;
});

const belowMinAmount = computed(() => {
  if (isPremium.value && isGroupOrder.value) return false;
  const maker = chosenMaker.value as Record<string, unknown>;
  const minOrder = Number(maker.min_order_amount || 0);
  const itemsGross = Number(cart.value?.items_gross || cart.value?.items_net || 0);
  return minOrder > 0 && (itemsGross / 100) < (minOrder / 100);
});

function toggleAccordionSection(section: string) {
  activeAccordionSection.value =
    activeAccordionSection.value === section ? null : section;
}

function getUniqueKey(item: Record<string, unknown>): string {
  const product = item.product as Record<string, unknown>;
  const modifiers = item.appliedModifiers as Record<string, unknown>[] | undefined;
  const modKey = modifiers
    ? modifiers.map((m) => `${m.modifier_id}:${m.quantity}`).join("-")
    : "";
  return `${product.id}_${modKey}`;
}

function updateCartQuantity(payload: {
  product: Record<string, unknown>;
  appliedModifiers?: unknown[];
}) {
  if (!cart.value) return;
  const cartItems = (cart.value as Cart).items;
  // Matched on the product AND its modifier set. Product id alone is not a line identity: two lines of
  // the same product with different add-ons both resolved to the first, so changing the quantity of one
  // resized the other — and now that the modifier quantities are rescaled against the resolved line's
  // quantity, the wrong line also supplies the wrong divisor. QuantityOperator does pass the row's
  // modifiers, so the identity is available here.
  const existingItem = cartItems.find(
    (item) =>
      item.product.id === payload.product.id &&
      isSameStoredModifierSet(
        item.appliedModifiers,
        payload.appliedModifiers as AppliedModifier[] | undefined,
      ),
  );
  if (!existingItem) return;

  const qty = Number(payload.product.quantity || 0);
  const minOrder = Number(payload.product.min_order_amount || 0);

  if (qty > 0 && (qty >= minOrder || !minOrder)) {
    // The line quantity is changing and existingItem.appliedModifiers are scaled to the OLD one, so
    // rescale. Passing them through would leave the add-on billed for the old line length.
    // See utils/lineModifierQuantities.ts.
    const rescaled = rescaleModifiersToLine(
      existingItem.appliedModifiers,
      existingItem.quantity,
      qty,
    );
    // rescaleModifiersToLine deliberately leaves split shares alone, so on a quantity change they can
    // still sum to the OLD line — which validateSplitQuantityModifiers rejects server-side with
    // ModSplitQtyMismatch, a 400 that the store swallows. The customer pressed a control and nothing
    // happened, with no message. Trimming here is the same job the product modal's updateModalQuantity
    // already does; `product.modifierGroups` is in scope (it is what QuantityOperator itself tests).
    const groups = (existingItem.product as Record<string, unknown>)?.modifierGroups as
      | { id: number; split_quantity?: boolean }[]
      | undefined;

    ordersStore.addToCart({
      product: existingItem.product,
      quantity: qty,
      appliedModifiers: groups?.length
        ? clampSplitQuantityModifiers(rescaled, groups, qty)
        : rescaled,
      maker: (cart.value as Cart).maker as any,
      date: (cart.value as Cart).date || false,
      order_product_id: existingItem.order_product_id || null,
      mode: "set",
      ...(isPremium.value ? { isPremium: true } : {}),
    });
  } else {
    removeCartItem(existingItem);
  }
}

function removeCartItem(item: CartItem) {
  if (!cart.value) return;
  ordersStore.removeFromCart({
    product: item.product,
    quantity: 0,
    appliedModifiers: item.appliedModifiers,
    maker: (cart.value as Cart).maker as any,
    date: (cart.value as Cart).date || false,
    ...(isPremium.value ? { isPremium: true } : {}),
  });
}

function chooseDeliveryDate(date: Record<string, unknown>) {
  ordersStore.setDeliveryDate({ payload: date as any, isPremium: isPremium.value });
  ordersStore.setDeliveryWindows(date as any);
}

function chooseDeliveryWindow(window: string) {
  ordersStore.setSelectedWindow({ payload: window, isPremium: isPremium.value });
}

async function updateShareLink() {
  const config = useRuntimeConfig();
  try {
    shareLink.value = false;
    // $fetch returns the response body directly — no axios-style `.data` wrapper
    const response = (await ordersStore.createShareCart(ordersStore.dealId || null)) as {
      id: string;
    };
    const shareUrl = encodeURI(
      `${config.public.mainUrl}/store/${cart.value?.maker?.slug}/?cart=${response.id}`,
    );
    await navigator.clipboard.writeText(shareUrl);
    shareLink.value = true;
    setTimeout(() => {
      shareLink.value = false;
    }, 3000);
  } catch {
    // share cart or clipboard write failed
  }
}

function handleCheckoutButtonClick() {
  if (isEditMode.value) {
    emit("checkout:start");
    return;
  }
  if (isCheckout.value) {
    ordersStore.setShowCart(false);
    navigateTo(-1 as any);
  } else {
    ordersStore.setShowCart(false);
    navigateTo("/checkout");
  }
}

// ── Tooltip logic (delivery / group order breakdown) ──
const isGroupOrder = computed(() => {
  if (!isPremium.value) return false;
  return !!premiumStore.selectedPremiumOrderConfig?.isGroupOrder;
});

const premiumOrderCredit = computed(() => {
  const storeCredit = premiumStore.premiumOrderCredit as Record<string, unknown> | false;
  if (storeCredit && typeof storeCredit === 'object') return storeCredit;
  return {} as Record<string, unknown>;
});

const premiumTotalsBreakdown = computed(() => {
  const credit = premiumOrderCredit.value;
  const fmt = (v: unknown) => (Number(v || 0) / 100).toFixed(2);
  return {
    credit_net: fmt(credit.credit_net),
    items_net: fmt(credit.items_net),
    items_vat: fmt(credit.items_vat),
    items_gross: fmt(credit.items_gross),
    employee_net: fmt(credit.employee_net),
    employee_vat: fmt(credit.employee_vat),
    employee_gross: fmt(credit.employee_gross),
    employer_net: fmt(credit.employer_net),
    employer_vat: fmt(credit.employer_vat),
    employer_gross: fmt(credit.employer_gross),
  };
});

// ── Premium guards ──
const isUserOwner = computed(() => {
  return authStore.user?.role === "owner";
});

const isEmployeeTopUpAllowed = computed(() => true);

const overCreditLimit = computed(() => {
  return (
    !isUserOwner.value &&
    isPremium.value &&
    isGroupOrder.value &&
    Number(premiumOrderCredit.value?.employee_gross || 0) > 0
  );
});

const overCreditLimitCopy = computed(() => {
  return isEmployeeTopUpAllowed.value
    ? "You've exceeded your credit limit. You will need to pay for the amount over your credit limit to place your order. Please confirm your order to proceed to payment."
    : "You've exceeded your company allowance.";
});

const belowStripeMin = computed(() => {
  const STRIPE_MIN_PENCE = 30;
  const employeeGross = Number(premiumOrderCredit.value?.employee_gross || 0);
  return employeeGross > 0 && employeeGross < STRIPE_MIN_PENCE;
});

// Using premiumStore.userOrderPaymentStatus directly

const premiumDisableSubmit = computed(() => {
  if (!isPremium.value) return false;
  if (!ordersStore.hasUnsavedChanges) return true;
  if (ordersStore.cartLoading) return true;
  if (ordersStore.cartError) return true;
  if (premiumStore.userOrderPaymentStatus === USER_ORDER_PAYMENT_STATUS.COMPLETE) return true;
  if (belowStripeMin.value) return true;
  if (!isEmployeeTopUpAllowed.value && overCreditLimit.value) return true;
  return false;
});

const editDisableSubmit = computed(() => {
	if (isGroupOrder.value) return false; // defer to premium guards for group orders
  if (!isEditMode.value) return false;
  if (!ordersStore.hasUnsavedChanges) return true;
  if (ordersStore.cartLoading) return true;
  if (ordersStore.cartError) return true;
  return false;
});

// ── Premium credit recalculation on cart changes ──
watch(
  () => cart.value,
  (to) => {
    if (isPremium.value && to) {
      premiumStore.calculatePremiumCredit({
        is_group: isGroupOrder.value,
        maker_id: (chosenMaker.value as Record<string, unknown>)?.id,
        products:
          to.items?.map((item: Record<string, unknown>) => ({
            product_id: (item.product as Record<string, unknown>)?.id,
            quantity: item.quantity,
            appliedModifiers: item.appliedModifiers,
          })) || [],
      });
    }
  },
  { deep: true, immediate: true },
);

// ── Flash item count when items are added ──
watch(
  items,
  (newItems, oldItems) => {
    const newCount = newItems?.reduce((sum: number, item: Record<string, unknown>) => sum + Number(item.quantity || 0), 0) || 0;
    const oldCount = oldItems?.reduce((sum: number, item: Record<string, unknown>) => sum + Number(item.quantity || 0), 0) || 0;
    if (newCount > oldCount) {
      itemsFlash.value = true;
      setTimeout(() => {
        itemsFlash.value = false;
      }, 600);
    }
  },
  { deep: true },
);

function showDeliveryTooltipHandler() {
  if (deliveryTooltipHideTimeout) {
    clearTimeout(deliveryTooltipHideTimeout);
    deliveryTooltipHideTimeout = null;
  }
  showDeliveryTooltip.value = true;
}
function hideDeliveryTooltipDebounced() {
  deliveryTooltipHideTimeout = setTimeout(() => {
    showDeliveryTooltip.value = false;
  }, 200);
}
function showBreakdownTooltip() {
  if (tooltipHideTimeout) {
    clearTimeout(tooltipHideTimeout);
    tooltipHideTimeout = null;
  }
  showFullPremBreakdownTooltip.value = true;
}
function hideBreakdownTooltipDebounced() {
  tooltipHideTimeout = setTimeout(() => {
    showFullPremBreakdownTooltip.value = false;
  }, 200);
}
</script>

<template>
  <div class="cart-summary">
    <div
      v-if="showCartItems && !isEditModeHydrating"
      class="cart-summary__wrapper"
    >
      <DynamicButton
        class="cart-summary__wrapper--close hide-desktop"
        :is-slot="true"
        color="transparent"
        @click-event="ordersStore.setShowCart(false)"
      >
        <template #button-body>
          <fa
            :icon="['fal', 'xmark']"
            class="icon"
          />
        </template>
      </DynamicButton>

      <div class="cart-summary__wrapper--header">
        <p class="order-with grey-text">
          Your order with
          <NuxtLink class="heavy" :to="`/store/${(cart as Record<string, unknown>)?.maker && ((cart as Record<string, unknown>).maker as Record<string, unknown>).slug}`">
            {{ (cart as Record<string, unknown>)?.maker && ((cart as Record<string, unknown>).maker as Record<string, unknown>).name }}
          </NuxtLink>
        </p>
        <DynamicButton
          v-if="!isEditMode && hasCartContext"
          icon="link"
          color="white"
          :text="shareLink ? 'Copied!' : 'Share'"
          width="35%"
          @click-event="updateShareLink"
        />
      </div>
      <DynamicButton
        v-if="cartIsLocked && (cart as Record<string, unknown>)?.maker"
        class="cart-summary__back-to-maker"
        icon="arrow-left"
        color="white"
        text="Back to Maker"
        width="100%"
        @click-event="backToMaker"
      />

      <div class="cart-summary__accordion-area">
        <!-- Date & Time accordion -->
        <div class="date-and-time-accordion cart-summary__accordion-section">
          <div
            class="cart-summary__accordion-header"
            @click="toggleAccordionSection('datetime')"
          >
            <div class="cart-summary__accordion-header--left">
              <fa
                :icon="['fal', 'calendar-days']"
                class="pink-text icon"
              />
              <span class="grey-text">Date & Time</span>
            </div>
            <div class="cart-summary__accordion-header--right">
              <span
                v-if="activeAccordionSection !== 'datetime'"
                class="light-grey-text small-text"
              >
                {{ dateTimeSummary }}
              </span>
              <fa
                :icon="[
                  'fal',
                  activeAccordionSection === 'datetime' ? 'chevron-up' : 'chevron-down',
                ]"
                class="pink-text small-icon"
              />
            </div>
          </div>
          <div
            v-if="activeAccordionSection === 'datetime'"
            class="cart-summary__accordion-content animate__animated animate__fadeIn animate__faster"
          >
            <div class="cart-summary__wrapper--pickers">
              <DatePicker
                class="date"
                :chosen-date="(cart as Record<string, unknown>)?.date"
                :fixed="true"
                :delivery-days="makerDeliveryDays || false"
                :disabled="cartIsLocked || isPremium"
                @return-date="chooseDeliveryDate"
              />
              <TimePicker
                class="time"
                :cart="cart"
                :fixed="true"
                :disabled="cartIsLocked || isPremium"
                @return-time="chooseDeliveryWindow"
              />
            </div>
          </div>
        </div>

        <!-- Items accordion -->
        <div class="items-accordion cart-summary__accordion-section">
          <div
            class="cart-summary__accordion-header"
            @click="toggleAccordionSection('items')"
          >
            <div class="cart-summary__accordion-header--left">
              <fa
                :icon="['fal', 'basket-shopping']"
                class="pink-text icon"
              />
              <span class="grey-text">Items</span>
            </div>
            <div class="cart-summary__accordion-header--right">
              <span
                v-if="activeAccordionSection !== 'items'"
                :class="['light-grey-text small-text', { 'items-flash': itemsFlash }]"
              >
                {{ itemsSummary }}
              </span>
              <fa
                :icon="[
                  'fal',
                  activeAccordionSection === 'items' ? 'chevron-up' : 'chevron-down',
                ]"
                class="pink-text small-icon"
              />
            </div>
          </div>
          <div
            v-if="activeAccordionSection === 'items'"
            class="cart-summary__accordion-content cart-summary__accordion-content--items animate__animated animate__fadeIn animate__faster"
          >
            <div class="cart-summary__wrapper--body">
              <div
                v-for="item in items"
                :key="getUniqueKey(item as Record<string, unknown>)"
                class="products"
              >
                <div class="products__name">
                  <p class="products__name--text grey-text small-text">
                    {{ (item as Record<string, unknown>).product &&
                      ((item as Record<string, unknown>).product as Record<string, unknown>).name }}
                    <span
                      v-if="
                        (item as Record<string, unknown>).appliedModifiers &&
                        ((item as Record<string, unknown>).appliedModifiers as Record<string, unknown>[]).length > 0
                      "
                    >
                      <i
                        v-for="(modifier, i) in ((item as Record<string, unknown>).appliedModifiers as Record<string, unknown>[])"
                        :key="`modifier_${i}`"
                        class="small-text light-grey-text"
                      >
                        {{ modifier.name }}
                      </i>
                    </span>
                  </p>
                </div>
                <div class="products__quantity">
                  <QuantityOperator
                    :disabled="cartIsLocked"
                    :disable-plus="isLineUnavailable(item as Record<string, unknown>)"
                    :product-quantity="Number((item as Record<string, unknown>).quantity || 1)"
                    :item="(item as Record<string, unknown>).product"
                    :modifiers="(item as Record<string, unknown>).appliedModifiers"
                    :min-quantity="
                      ((item as Record<string, unknown>).product as Record<string, unknown>)
                        ?.min_order_amount
                        ? Number(
                            ((item as Record<string, unknown>).product as Record<string, unknown>)
                              .min_order_amount,
                          )
                        : 1
                    "
                    class="quantity"
                    @return-quantity="updateCartQuantity"
                  />
                </div>
                <div class="products__price">
                  <p class="grey-text heavy">
                    {{
                      `£${parseFloat(
                        String(
                          Number(
                            ((item as Record<string, unknown>).product as Record<string, unknown>)
                              ?.price_gross || 0,
                          ) / 100,
                        ),
                      ).toFixed(2)}`
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Order Breakdown accordion -->
        <div
          v-if="showFullBreakdown || (cart && Number((cart as Record<string, unknown>).items_net || 0) > 0)"
          class="order-breakdown-accordion cart-summary__accordion-section"
        >
          <div
            class="cart-summary__accordion-header"
            @click="toggleAccordionSection('totals')"
          >
            <div class="cart-summary__accordion-header--left">
              <fa
                :icon="['fal', 'receipt']"
                class="pink-text icon"
              />
              <span class="grey-text">Order Breakdown</span>
            </div>
            <div class="cart-summary__accordion-header--right">
              <fa
                :icon="[
                  'fal',
                  activeAccordionSection === 'totals' ? 'chevron-up' : 'chevron-down',
                ]"
                class="pink-text small-icon"
              />
            </div>
          </div>
          <div
            v-if="activeAccordionSection === 'totals'"
            class="cart-summary__accordion-content animate__animated animate__fadeIn animate__faster"
          >
            <div class="cart-summary__wrapper--totals">
              <div v-if="isGroupOrder">
                <p class="grey-text small-text">
                  <span class="grey-text">
                    Subtotal
                    <span class="light-grey-text">(exc VAT)</span>
                    :
                  </span>
                  <span class="heavy">£{{ premiumTotalsBreakdown.items_net }}</span>
                </p>
                <p class="grey-text small-text">
                  <span class="grey-text">
                    Employer allowance
                    <span class="light-grey-text">(exc VAT)</span>
                    :
                  </span>
                  <span class="heavy">£{{ premiumTotalsBreakdown.credit_net }}</span>
                </p>
                <p class="grey-text small-text">
                  <span class="grey-text">
                    Amount to pay
                    <span class="light-grey-text">(exc VAT)</span>
                    :
                  </span>
                  <span class="heavy">£{{ premiumTotalsBreakdown.employee_net }}</span>
                </p>
                <p class="grey-text small-text">
                  <span>VAT:</span>
                  <span class="heavy">£{{ premiumTotalsBreakdown.employee_vat }}</span>
                </p>
              </div>
              <div v-else>
                <p class="grey-text small-text">
                  <span class="grey-text">{{ subtotal.title }}</span>
                  <span class="heavy">{{ subtotal.value }}</span>
                </p>
                <template v-if="showFullBreakdown">
                  <p
                    v-if="deliveryFee"
                    class="grey-text small-text"
                  >
                    <span class="grey-text">Delivery:</span>
                    <span class="heavy">{{ deliveryFee }}</span>
                  </p>
                  <p
                    v-if="serviceFee && !isPremium"
                    class="grey-text small-text"
                  >
                    <span class="grey-text">Service Fee{{ serviceFeeRate ? ` (${serviceFeeRate})` : '' }}:</span>
                    <span class="heavy">{{ serviceFee }}</span>
                  </p>
                </template>
              </div>
              <p
                v-if="!isPremium && showFullBreakdown"
                class="grey-text small-text"
              >
                <span class="grey-text">VAT:</span>
                <span class="heavy">£{{ displayVat }}</span>
              </p>
              <p
                v-if="!isPremium && discountAmount"
                class="grey-text small-text"
              >
                <span class="grey-text">Discount:</span>
                <span class="heavy">{{ discountAmount }}</span>
              </p>
              <div v-if="isPremium && !isGroupOrder">
                <p class="light-grey-text small-text">
                  <span class="light-grey-text">Minimum order value:</span>
                  <span class="heavy">£{{ minOrderVal.toFixed(2) }}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="cart-summary__wrapper--footer">
        <div
          v-if="cart && (cart as Record<string, unknown>).total"
          class="cart-summary__footer-total"
        >
          <div class="total grey-text">
            <span class="total__title grey-text heavy">Total:</span>
            <div class="total__value heavy">
              <span>{{ cartTotal }}</span>
            </div>
          </div>
          <!-- Tooltip triggers -->
          <span
            v-if="isGroupOrder"
            class="total__tooltip tooltip__icon small-text light-grey-text"
            @mousedown="showBreakdownTooltip"
            @mouseover="showBreakdownTooltip"
            @mouseleave="hideBreakdownTooltipDebounced"
          >
            See full breakdown
          </span>
          <span
            v-if="!isPremium && deliveryFee"
            class="total__tooltip tooltip__icon small-text light-grey-text"
            @mousedown="showDeliveryTooltipHandler"
            @mouseover="showDeliveryTooltipHandler"
            @mouseleave="hideDeliveryTooltipDebounced"
          >
            See delivery breakdown
          </span>
          <!-- Delivery breakdown tooltip -->
          <div
            v-if="!isPremium && showDeliveryTooltip"
            class="tooltip tooltip--upward box-shadow"
            @mouseover="showDeliveryTooltipHandler"
            @mouseleave="hideDeliveryTooltipDebounced"
          >
            <p class="grey-text small-text">
              <span class="grey-text">Delivery (exc VAT):</span>
              <span class="heavy">£{{ (Number((cart as Record<string, unknown>)?.delivery_net || 0) / 100).toFixed(2) }}</span>
            </p>
            <p class="grey-text small-text">
              <span class="light-grey-text">VAT:</span>
              <span class="light-grey-text heavy">£{{ (Number((cart as Record<string, unknown>)?.delivery_vat || 0) / 100).toFixed(2) }}</span>
            </p>
            <hr />
            <p class="grey-text small-text">
              <span class="grey-text">Delivery (inc VAT):</span>
              <span class="heavy">£{{ ((Number((cart as Record<string, unknown>)?.delivery_net || 0) + Number((cart as Record<string, unknown>)?.delivery_vat || 0)) / 100).toFixed(2) }}</span>
            </p>
            <hr />
            <p class="light-grey-text small-text" style="display: block">
              At &amp;Dine, we offer flexible 30-minute delivery windows, seven days a week. Our
              delivery fee is based on the value of your food order: £35 under £400, £50 from £400
              to £2,000, and £65 over £2,000, all + VAT. You'll see the exact fee at checkout, so
              there are no surprises.
            </p>
            <p class="light-grey-text small-text" style="display: block">
              For Group Orders, delivery costs are based on the order value, vehicle requirements,
              equipment and packaging needs. The fee at checkout reflects your expected headcount
              and budget, and may vary depending on the final details of your order.
            </p>
            <p class="light-grey-text small-text" style="display: block">
              If you have any questions, feel free to get in touch!
            </p>
          </div>
          <!-- Group order breakdown tooltip -->
          <div
            v-if="isGroupOrder && showFullPremBreakdownTooltip"
            class="tooltip tooltip--upward box-shadow"
            @mouseover="showBreakdownTooltip"
            @mouseleave="hideBreakdownTooltipDebounced"
          >
            <p class="grey-text small-text">
              <span class="grey-text">Subsidised by your business (exc VAT):</span>
              <span class="heavy">£{{ premiumTotalsBreakdown.credit_net }}</span>
            </p>
            <hr />
            <p class="grey-text small-text">
              <span class="grey-text">Subtotal (exc VAT):</span>
              <span class="heavy">£{{ premiumTotalsBreakdown.items_net }}</span>
            </p>
            <p class="grey-text small-text">
              <span class="light-grey-text">VAT:</span>
              <span class="light-grey-text heavy">£{{ premiumTotalsBreakdown.items_vat }}</span>
            </p>
            <p class="grey-text small-text">
              <span class="grey-text">Subtotal (inc VAT):</span>
              <span class="heavy">£{{ premiumTotalsBreakdown.items_gross }}</span>
            </p>
            <hr />
            <p class="grey-text small-text">
              <span class="grey-text">Employer allowance (exc VAT):</span>
              <span class="heavy">£{{ premiumTotalsBreakdown.employer_net }}</span>
            </p>
            <p class="grey-text small-text">
              <span class="light-grey-text">VAT:</span>
              <span class="light-grey-text heavy">£{{ premiumTotalsBreakdown.employer_vat }}</span>
            </p>
            <p class="grey-text small-text">
              <span class="grey-text">Employer allowance (inc VAT):</span>
              <span class="heavy">£{{ premiumTotalsBreakdown.employer_gross }}</span>
            </p>
            <hr />
            <p class="grey-text small-text">
              <span class="grey-text">Amount to pay (exc VAT):</span>
              <span class="heavy">£{{ premiumTotalsBreakdown.employee_net }}</span>
            </p>
            <p class="grey-text small-text">
              <span class="light-grey-text">VAT:</span>
              <span class="light-grey-text heavy">£{{ premiumTotalsBreakdown.employee_vat }}</span>
            </p>
            <p class="grey-text small-text">
              <span class="grey-text">Amount to pay (inc VAT):</span>
              <span class="heavy">£{{ premiumTotalsBreakdown.employee_gross }}</span>
            </p>
            <hr />
            <p class="grey-text small-text">
              <span class="grey-text">Total (inc VAT):</span>
              <span class="heavy">{{ cartTotal }}</span>
            </p>
          </div>
        </div>
        <BannerError
          v-if="isPremium && !ordersStore.cartError && ordersStore.hasUnsavedChanges && overCreditLimit && !belowStripeMin"
          variant="warning"
          message="Don't forget to submit your order! Any changes you've made will be lost if you leave without saving."
        />
        <BannerError :message="overCreditLimit ? overCreditLimitCopy : ''" />
        <BannerError
				v-if="overCreditLimit && belowStripeMin"
          :message="
            belowStripeMin
              ? 'You must spend at least £0.30 to place an order over your allocated credit.'
              : ''
          "
        />
        <BannerError
          :message="
            belowMinAmount
              ? `Your subtotal is below the minimum order value of £${minOrderVal.toFixed(2)}`
              : ''
          "
        />
        <BannerError
          :message="
            ordersStore.cartError && !belowMinAmount
              ? String(ordersStore.cartError)
              : ''
          "
        />
        <!-- Names the offending dishes rather than saying "some items", so the customer
             doesn't have to hunt for them. Not a locking overlay: the line can still be
             removed or reduced, just not increased. -->
        <BannerError
          v-if="orderabilityBlockedEffective"
          variant="warning"
          :message="
            unavailableProductNames
              ? `${unavailableProductNames} ${unavailableProductNameList.length === 1 ? 'is' : 'are'} no longer available on this menu — remove ${unavailableProductNameList.length === 1 ? 'it' : 'them'} to check out.`
              : 'Some items in this cart are no longer available because they violate organisation rules. Remove them before checking out.'
          "
        />
        <DynamicButton
          v-if="isMobile || !isCheckout"
          class="next"
          color="pink"
          :text="
            isEditMode && !isPremium
              ? 'Confirm Changes'
              : isPremium
                ? 'Confirm Order'
                : `${isCheckout ? 'Back' : 'Go'} to Checkout`
          "
          width="100%"
          :icon="`${
            ordersStore.cartLoading
              ? 'spinner'
              : isCheckout
                ? 'arrow-left'
                : 'arrow-right'
          }`"
          :disabled="disableInputs || orderabilityBlockedEffective || belowMinAmount || editDisableSubmit || premiumDisableSubmit || !((cart as Record<string, unknown>)?.date as Record<string, unknown> | undefined)?.selectedWindow"
          :spinning="ordersStore.cartLoading"
          @click-event="handleCheckoutButtonClick"
        />
      </div>
    </div>
    <div
      v-else-if="isEditModeHydrating"
      class="cart-summary__empty"
    >
      <DynamicButton
        class="cart-summary__wrapper--close hide-desktop"
        :is-slot="true"
        color="transparent"
        @click-event="ordersStore.setShowCart(false)"
      >
        <template #button-body>
          <fa
            :icon="['fal', 'xmark']"
            class="icon"
          />
        </template>
      </DynamicButton>
      <fa
        :icon="['fal', 'spinner']"
        class="cart-summary__empty--icon fa-spin"
        spin
      />
      <h4 class="cart-summary__text">Loading your order...</h4>
    </div>
    <div
      v-else
      class="cart-summary__empty"
    >
      <DynamicButton
        class="cart-summary__wrapper--close hide-desktop"
        :is-slot="true"
        color="transparent"
        @click-event="ordersStore.setShowCart(false)"
      >
        <template #button-body>
          <fa
            :icon="['fal', 'xmark']"
            class="icon"
          />
        </template>
      </DynamicButton>
      <div class="cart-summary__empty--icon-wrap">
        <fa
          :icon="['fal', 'basket-shopping']"
          class="cart-summary__empty--icon"
        />
      </div>
      <h4 class="cart-summary__text">Your basket is empty</h4>
      <p class="cart-summary__empty--subtitle light-grey-text">
        Browse the menu and add dishes<template v-if="(chosenMaker as Record<string, unknown>).name"> from {{ (chosenMaker as Record<string, unknown>).name }}</template> to start your order.
      </p>
    </div>
  </div>
</template>

<style lang="scss">
.cart-summary {
  @media (max-width: $tablet) {
    height: 100%;
  }

  &__back-to-maker {
    margin: 0.5rem 0 1rem;
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    max-height: 65vh;
    overflow: hidden;
    padding-bottom: 4px;
    @media (max-width: $tablet) {
      max-height: none;
      height: 100%;
      overflow: hidden;
    }

    &--close {
      padding: 0;
      display: flex;
      align-self: flex-start;
      justify-content: flex-start;
      width: auto;
      color: var(--color-grey-1);
      cursor: pointer;
    }

    &--header {
      flex-shrink: 0;
      display: flex;
      z-index: 4444;
      align-items: center;
      width: 99%;
      justify-content: space-between;
      padding-bottom: 1rem;
      .order-with {
        width: 63%;
      }
    }

    &--pickers {
      display: flex;
      padding: 0.5rem;

      .date,
      .time {
        margin-right: 1rem;
      }

      // Mobile: pickers share the row evenly so they don't overlap or wrap.
      // Desktop keeps the original content-sized layout flush-left.
      @media (max-width: $tablet) {
        gap: 0.5rem;

        .date,
        .time {
          flex: 1;
          min-width: 0;
          margin-right: 0;
        }
      }
    }

    &--body {
      border-top: 2px solid var(--color-grey-3);

      .products {
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-bottom: 2px solid var(--color-grey-3);
        padding: 0.5rem;
        width: calc(100% - 1rem);

        &:last-child {
          border-bottom: none;
        }

        &__name {
          display: flex;
          width: 70%;
          align-items: center;
          justify-content: flex-start;

          &--remove {
            color: var(--color-pink-1);
            cursor: pointer;
            margin-right: 0.5rem;
          }

          &--text {
            width: 85%;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            margin: 0;
            word-wrap: break-word;
            font-family: inherit;

            span {
              margin: 0;
            }

            i {
              display: flex;
              flex-direction: column;
              font-size: 0.6rem !important;
              line-height: 0.8rem !important;
            }
          }
        }

        &__quantity {
          display: flex;
          align-items: center;
          padding: 0 0.5rem;

          .quantity {
            transform: scale(0.85);
          }
        }

        &__price {
          min-width: 3.5rem;
          text-align: right;

          p {
            margin: 0;
          }
        }
      }
    }

    &--totals {
      padding: 0.5rem;
      width: calc(100% - 1rem);
      position: relative;

      .info {
        cursor: pointer;
      }

      .tooltip__icon {
        cursor: pointer;
      }

      .fee {
        display: flex;
        align-items: center;
        justify-content: flex-start;
      }

      .estimate {
        display: block;
      }

      p {
        display: flex;
        justify-content: space-between;
        margin: 0.2rem 0;

        span {
          margin: 0;
        }
      }

      .delivery {
        display: flex;
        align-items: center;
        justify-content: flex-start;
      }

      .tooltip {
        position: absolute;
        background-color: var(--color-yellow-2);
        padding: 0.5rem;
        border-radius: 0.4rem;
        height: auto;
        width: calc(100% - 1rem);
        bottom: 100%;
        left: 0;
        z-index: 9999;
        max-height: 40vh;
        overflow-y: auto;

        &__title {
          font-weight: bold;
          margin-bottom: 0.5rem;
        }
      }
    }

    &--footer {
      flex-shrink: 0;
      position: relative;
      display: flex;
      flex-direction: column;
      background-color: var(--color-white-1);
      margin-top: 1rem;
      padding: 0 5px 4px;
      overflow: visible;
      width: calc(100% - 1rem);

      .bar {
        width: 98%;
        padding: 1% 0;
      }

      .next {
        margin: 1rem 0 0;
      }

      .error {
        margin: 0;
      }
    }
  }

  // Accordion area
  &__accordion-area {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
  }

  &__accordion-section {
    border-bottom: 2px solid var(--color-grey-3);

    &:first-child {
      border-top: 2px solid var(--color-grey-3);
    }
  }

  &__accordion-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    padding: 0.6rem 0.5rem;
    user-select: none;

    &:hover {
      background-color: var(--color-pink-4);
      border-radius: 0;
    }

    &--left {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    &--right {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      span {
        max-width: 12rem;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        transition:
          color 0.4s ease-out,
          font-weight 0.4s ease-out;
      }

      .items-flash {
        color: var(--color-pink-1) !important;
        font-weight: 600;
      }
    }
  }

  &__accordion-content {
    overflow: hidden;

    &--items .cart-summary__wrapper--body {
      height: auto;
    }
  }

  // Footer pinned total
  &__footer-pre-order {
    padding: 0.5rem;
    width: calc(100% - 1rem);

    p {
      display: flex;
      justify-content: space-between;
      margin: 0;

      span {
        margin: 0;
      }
    }

    .estimate {
      display: block;
      margin-top: 0.25rem;
    }
  }

  // Footer total
  &__footer-total {
    position: relative;
    padding: 0.5rem;
    width: calc(100% - 1rem);

    .total {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;

      &__title {
        margin: 0;
      }

      &__value {
        display: flex;
        flex-direction: column;
        align-items: flex-end;

        & span {
          margin: 0;
        }
      }

      &__tooltip {
        cursor: pointer;
        text-decoration: underline;
        margin-top: 0.25rem;
      }
    }

    .tooltip__icon {
      cursor: pointer;
    }

    .tooltip {
      position: absolute;
      bottom: 100%;
      left: 0;
      width: calc(100% - 1rem);
      background-color: var(--color-yellow-2);
      padding: 0.5rem;
      border-radius: 0.4rem;
      height: auto;
      max-height: 40vh;
      overflow-y: auto;
      z-index: 9999;

      &__title {
        font-weight: bold;
        margin-bottom: 0.5rem;
      }

      p {
        display: flex;
        justify-content: space-between;
        margin: 0.15rem 0;

        span {
          margin: 0;
        }
      }

      hr {
        border: none;
        border-top: 1px solid var(--color-grey-3);
        margin: 0.4rem 0;
      }
    }
  }

  &__empty {
    text-align: center;
    padding: 10% 0;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;

    // Small basket icon sits inside a soft grey disc. Reads as an empty
    // illustration rather than a giant pictogram filling the panel.
    &--icon-wrap {
      width: 5rem;
      height: 5rem;
      border-radius: 50%;
      background: var(--color-grey-4);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 1.5rem;
    }
    &--icon {
      font-size: 2rem;
      color: var(--color-grey-2);
    }
    &--subtitle {
      margin: 0.5rem 0 0;
      max-width: 22rem;
      font-size: var(--text-sm);
      line-height: 1.4;
    }
  }

  .disable-icon {
    color: var(--color-grey-2);
  }
}
</style>
